/* ═══════════════════════════════════════════════════════════════
   widgetEvaluator — pure reduction of a CustomWidgetDefinition against
   already-loaded source rows. No I/O: the caller (widget builder /
   renderer) fetches rows via a WidgetSource's `load()` and hands them
   here. Filtering reuses filterModel's `matchesFilterState` — the SAME
   predicate every DataTable in the app evaluates rows with, so a widget's
   filter behaves identically to a table's.
   ═══════════════════════════════════════════════════════════════ */

import { matchesFilterState } from '../components/filterModel';
import type { CustomWidgetDefinition, WidgetMeasure } from '../../shared/entities';
import { isRowComplete, type WidgetSource } from './widgetSources';

export type WidgetEvalResult =
  | { kind: 'number'; value: number }
  | { kind: 'list'; rows: Record<string, unknown>[]; total: number }
  | { kind: 'bar'; data: { label: string; value: number }[] }
  | { kind: 'meter'; value: number; target: number; label: string }
  | { kind: 'line'; points: { key: string; label: string; value: number }[] };

const DEFAULT_LIST_MAX = 10;

/** A line chart's x-axis is generated by walking every bucket between the
 *  earliest and latest matching row, even ones with zero rows — a filter
 *  spanning decades (e.g. 2026-01-01..9999-12-31, no upper bound picked)
 *  would otherwise walk ~416k weekly buckets. Cap the series and keep the
 *  LAST `LINE_MAX_BUCKETS` buckets ending at the max date, dropping the
 *  earlier ones — a line chart reads as "recent history," so trimming the
 *  tail end (not centering or trimming the head) keeps what a viewer
 *  actually wants to see. */
const LINE_MAX_BUCKETS = 520;

/** Sheet-offered page sizes (Builder R2). A legacy/out-of-range stored
 *  `max` (pre-dating this constraint, or a future shape drifting) clamps
 *  UP to the next tier rather than throwing or silently keeping an
 *  arbitrary cap. */
const LIST_MAX_TIERS = [5, 10, 25] as const;
export function clampListMax(max: number | undefined): number {
  if (max == null) return DEFAULT_LIST_MAX;
  for (const tier of LIST_MAX_TIERS) {
    if (max <= tier) return tier;
  }
  return LIST_MAX_TIERS[LIST_MAX_TIERS.length - 1];
}

// ─── Line bucketing (week/month) — no date-fns: it is NOT an installed
// dependency (the plan assumed it was; confirmed absent via package.json/
// node_modules, so this is hand-written, pure, and unit-tested). ───

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function startOfWeekMonday(d: Date): Date {
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addWeeks(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n * 7);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isoDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function weekLabel(d: Date): string {
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}
function monthLabel(d: Date): string {
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

// Date-only strings ("2026-09-01") parse as UTC midnight in the native
// `Date` constructor, which is the PREVIOUS day's evening in any western
// timezone — bucketing would shift by a day depending on the machine's
// zone. Same fix filterModel's `parseDateValue` applies: parse date-only
// strings as LOCAL midnight; anything else (a full timestamp) falls back
// to the platform parser.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseRowDate(raw: string): Date | null {
  const m = DATE_ONLY_RE.exec(raw.trim());
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/** Row → display value for a field, the shape `matchesFilterState` needs.
 *  Arrays (tags, multiselect columns) flatten to a comma string so
 *  `contains`/`anyOf` still work against them. */
function resolveField(
  row: Record<string, unknown>,
  field: string
): string | number | null | undefined {
  const v = row[field];
  if (v === undefined || v === null) return v as null | undefined;
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

/** Numeric coercion for sum/avg — non-numeric and missing values are
 *  excluded (never treated as 0), so an avg over a field with holes
 *  reflects only the rows that actually have a value. */
function numericValue(row: Record<string, unknown>, field: string): number | null {
  const v = row[field];
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/** True when ANY rule in the filter tree (top-level or inside a group)
 *  uses the `overdue` op — signals that the source's `completeField`
 *  refinement applies (filterModel itself only ever checks the date half:
 *  "before today"; it has no row/source context to know what "complete"
 *  means for an arbitrary table). AND-only today (OR-group filters are a
 *  separate, out-of-scope contract per the R2 plan), so this is a single
 *  filter-wide flag rather than a per-row/per-rule distinction. */
function filterUsesOverdue(filter: CustomWidgetDefinition['filter']): boolean {
  const flat = filter.rules.flatMap((r) => ('rules' in r ? r.rules : [r]));
  return flat.some((r) => r.op === 'overdue');
}

function filterRows(
  rows: Record<string, unknown>[],
  filter: CustomWidgetDefinition['filter'],
  source: WidgetSource,
  now: Date
): Record<string, unknown>[] {
  const matched = rows.filter((row) =>
    matchesFilterState(filter, (field) => resolveField(row, field), now)
  );
  if (!filterUsesOverdue(filter) || !source.completeField) return matched;
  return matched.filter((row) => !isRowComplete(row, source));
}

function reduceMeasure(rows: Record<string, unknown>[], measure: WidgetMeasure): number {
  if (measure === 'count') return rows.length;
  const values = rows
    .map((r) => numericValue(r, measure.field))
    .filter((v): v is number => v !== null);
  if (values.length === 0) return 0;
  switch (measure.op) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return values.reduce((a, b) => (b < a ? b : a), values[0]);
    case 'max':
      return values.reduce((a, b) => (b > a ? b : a), values[0]);
    default:
      return 0;
  }
}

function targetLabel(
  target: Extract<CustomWidgetDefinition['form'], { kind: 'meter' }>['target']
): string {
  return target.kind === 'fixed' ? `of ${target.value}` : `of ${target.op} ${target.field}`;
}

function evaluateLine(
  rows: Record<string, unknown>[],
  form: Extract<CustomWidgetDefinition['form'], { kind: 'line' }>,
  knownFields: Set<string>
): WidgetEvalResult {
  if (!knownFields.has(form.dateField)) return { kind: 'line', points: [] };
  const buckets = new Map<string, Record<string, unknown>[]>();
  let minStart: Date | null = null;
  let maxStart: Date | null = null;
  for (const row of rows) {
    const raw = row[form.dateField];
    if (raw == null) continue;
    const d = parseRowDate(String(raw));
    if (!d) continue;
    const bucketStart = form.bucket === 'week' ? startOfWeekMonday(d) : startOfMonth(d);
    const key = isoDateOnly(bucketStart);
    const list = buckets.get(key);
    if (list) list.push(row);
    else buckets.set(key, [row]);
    if (!minStart || bucketStart < minStart) minStart = bucketStart;
    if (!maxStart || bucketStart > maxStart) maxStart = bucketStart;
  }
  if (!minStart || !maxStart) return { kind: 'line', points: [] };
  if (form.bucket === 'week') {
    const spanWeeks = Math.round(
      (maxStart.getTime() - minStart.getTime()) / (7 * 24 * 60 * 60 * 1000)
    );
    if (spanWeeks + 1 > LINE_MAX_BUCKETS) {
      minStart = addWeeks(maxStart, -(LINE_MAX_BUCKETS - 1));
    }
  } else {
    const spanMonths =
      (maxStart.getFullYear() - minStart.getFullYear()) * 12 +
      (maxStart.getMonth() - minStart.getMonth());
    if (spanMonths + 1 > LINE_MAX_BUCKETS) {
      minStart = addMonths(maxStart, -(LINE_MAX_BUCKETS - 1));
    }
  }
  const points: { key: string; label: string; value: number }[] = [];
  let cursor = minStart;
  while (cursor <= maxStart) {
    const key = isoDateOnly(cursor);
    const bucketRows = buckets.get(key) ?? [];
    points.push({
      key,
      label: form.bucket === 'week' ? weekLabel(cursor) : monthLabel(cursor),
      value: reduceMeasure(bucketRows, form.measure),
    });
    cursor = form.bucket === 'week' ? addWeeks(cursor, 1) : addMonths(cursor, 1);
  }
  return { kind: 'line', points };
}

/** `source` grounds the definition's field references against the real
 *  catalog — a stale/removed field name degrades to an empty/zero result
 *  instead of throwing, since a saved widget must never crash a dashboard. */
export function evaluateWidget(
  def: CustomWidgetDefinition,
  rows: Record<string, unknown>[],
  source: WidgetSource,
  now: Date = new Date()
): WidgetEvalResult {
  const knownFields = new Set(source.fields.map((f) => f.key));
  const filtered = filterRows(rows, def.filter, source, now);

  if (def.form.kind === 'number') {
    return { kind: 'number', value: reduceMeasure(filtered, def.form.measure) };
  }

  if (def.form.kind === 'list') {
    const { sort, max } = def.form;
    let list = [...filtered];
    if (sort && knownFields.has(sort.field)) {
      const dir = sort.dir === 'desc' ? -1 : 1;
      list.sort((a, b) => {
        const av = resolveField(a, sort.field);
        const bv = resolveField(b, sort.field);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    }
    const total = list.length;
    const cap = clampListMax(max);
    // Full rows, not just `form.fields` — a caller (custom-widget.tsx,
    // Builder R2) needs the whole row for its row-drill (source.drill.row
    // often reads a field, e.g. `id`, that isn't one of the display
    // columns the user picked). Which fields to actually SHOW is a
    // rendering concern (renderEvalResult already receives `form.fields`).
    return { kind: 'list', rows: list.slice(0, cap), total };
  }

  if (def.form.kind === 'meter') {
    const { measure, target } = def.form;
    const value = reduceMeasure(filtered, measure);
    const targetValue =
      target.kind === 'fixed'
        ? target.value
        : reduceMeasure(filtered, { op: target.op, field: target.field });
    return { kind: 'meter', value, target: targetValue, label: targetLabel(target) };
  }

  if (def.form.kind === 'line') {
    return evaluateLine(filtered, def.form, knownFields);
  }

  // bar
  const { groupBy, measure } = def.form;
  if (!knownFields.has(groupBy)) return { kind: 'bar', data: [] };
  const groups = new Map<string, Record<string, unknown>[]>();
  for (const row of filtered) {
    const label = String(resolveField(row, groupBy) ?? '(none)');
    const bucket = groups.get(label);
    if (bucket) bucket.push(row);
    else groups.set(label, [row]);
  }
  const data = Array.from(groups.entries()).map(([label, groupRows]) => ({
    label,
    value: reduceMeasure(groupRows, measure),
  }));
  return { kind: 'bar', data };
}

/** Builder R2 — the rows behind ONE bar/line bucket, for the in-widget
 *  drill-down (custom-widget.tsx swaps the chart body for a `WList` of
 *  these instead of navigating cross-page — "robust across every source,
 *  no page-level filter plumbing," per the T4b lead ruling). `key` is the
 *  bar's label (bar's grouping IS its label) or the line point's bucket
 *  `key` (the iso bucket-start date, not its display label). Re-runs the
 *  SAME filter + bucketing the read side used, so the rows always match
 *  what was charted. Non-bar/line forms return []. */
export function rowsInBucket(
  def: CustomWidgetDefinition,
  rows: Record<string, unknown>[],
  source: WidgetSource,
  key: string,
  now: Date = new Date()
): Record<string, unknown>[] {
  const filtered = filterRows(rows, def.filter, source, now);
  if (def.form.kind === 'bar') {
    const { groupBy } = def.form;
    return filtered.filter((row) => String(resolveField(row, groupBy) ?? '(none)') === key);
  }
  if (def.form.kind === 'line') {
    const { dateField, bucket } = def.form;
    return filtered.filter((row) => {
      const raw = row[dateField];
      if (raw == null) return false;
      const d = parseRowDate(String(raw));
      if (!d) return false;
      const bucketStart = bucket === 'week' ? startOfWeekMonday(d) : startOfMonth(d);
      return isoDateOnly(bucketStart) === key;
    });
  }
  return [];
}
